import { defineStore } from 'pinia'
import { ref, computed, onUnmounted } from 'vue'
import type { ScheduleEntry, CreateScheduleEntryRequest, UpdateScheduleEntryRequest } from '../types/schedule'
import type { SearchFilters } from '../types/search'
import { scheduleService, type ConflictError } from '../services/ScheduleService'


export const useScheduleStore = defineStore('schedule', () => {
  // State
  const scheduleEntries = ref<ScheduleEntry[]>([])
  const selectedScheduleEntry = ref<ScheduleEntry | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const conflictError = ref<ConflictError | null>(null)
  const searchFilters = ref<SearchFilters>({})
  const currentDateRange = ref<{ startDate: Date; endDate: Date } | null>(null)


  // Getters
  const scheduleEntryCount = computed(() => scheduleEntries.value.length)

  const todayScheduleEntries = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    return scheduleEntries.value.filter(entry => {
      const startDate = new Date(entry.startDate)
      const endDate = new Date(entry.endDate)
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(0, 0, 0, 0)
      
      // Entry is active today if it spans today
      return startDate <= today && endDate >= today
    })
  })

  const upcomingScheduleEntries = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    return scheduleEntries.value.filter(entry => {
      const entryEndDate = new Date(entry.endDate)
      entryEndDate.setHours(0, 0, 0, 0)
      return entryEndDate >= today
    }).sort((a, b) => {
      const dateCompare = a.startDate.getTime() - b.startDate.getTime()
      if (dateCompare !== 0) return dateCompare
      return a.startTime.localeCompare(b.startTime)
    })
  })

  const pastScheduleEntries = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    return scheduleEntries.value.filter(entry => {
      const entryStartDate = new Date(entry.startDate)
      entryStartDate.setHours(0, 0, 0, 0)
      return entryStartDate < today
    }).sort((a, b) => {
      const dateCompare = b.startDate.getTime() - a.startDate.getTime() // Reverse order for past entries
      if (dateCompare !== 0) return dateCompare
      return b.startTime.localeCompare(a.startTime)
    })
  })

  const filteredScheduleEntries = computed(() => {
    let filtered = scheduleEntries.value

    // Apply vehicle filter
    if (searchFilters.value.vehicleId) {
      filtered = filtered.filter(entry => entry.vehicleId === searchFilters.value.vehicleId)
    }

    // Apply driver filter
    if (searchFilters.value.driverId) {
      filtered = filtered.filter(entry => entry.driverId === searchFilters.value.driverId)
    }

    // Apply date range filter
    if (searchFilters.value.startDate || searchFilters.value.endDate) {
      filtered = filtered.filter(entry => {
        const entryStartDate = new Date(entry.startDate)
        const entryEndDate = new Date(entry.endDate)
        entryStartDate.setHours(0, 0, 0, 0)
        entryEndDate.setHours(0, 0, 0, 0)

        if (searchFilters.value.startDate) {
          const startDate = new Date(searchFilters.value.startDate)
          startDate.setHours(0, 0, 0, 0)
          // Entry must end on or after filter start date
          if (entryEndDate < startDate) return false
        }

        if (searchFilters.value.endDate) {
          const endDate = new Date(searchFilters.value.endDate)
          endDate.setHours(0, 0, 0, 0)
          // Entry must start on or before filter end date
          if (entryStartDate > endDate) return false
        }

        return true
      })
    }

    // Sort by start date and time
    return filtered.sort((a, b) => {
      const dateCompare = a.startDate.getTime() - b.startDate.getTime()
      if (dateCompare !== 0) return dateCompare
      return a.startTime.localeCompare(b.startTime)
    })
  })

  const scheduleEntriesByVehicle = computed(() => {
    const grouped: Record<string, ScheduleEntry[]> = {}
    
    scheduleEntries.value.forEach(entry => {
      if (!grouped[entry.vehicleId]) {
        grouped[entry.vehicleId] = []
      }
      grouped[entry.vehicleId].push(entry)
    })

    // Sort entries within each vehicle group
    Object.keys(grouped).forEach(vehicleId => {
      grouped[vehicleId].sort((a, b) => {
        const dateCompare = a.startDate.getTime() - b.startDate.getTime()
        if (dateCompare !== 0) return dateCompare
        return a.startTime.localeCompare(b.startTime)
      })
    })

    return grouped
  })

  const scheduleEntriesByDriver = computed(() => {
    const grouped: Record<string, ScheduleEntry[]> = {}
    
    scheduleEntries.value.forEach(entry => {
      if (!grouped[entry.driverId]) {
        grouped[entry.driverId] = []
      }
      grouped[entry.driverId].push(entry)
    })

    // Sort entries within each driver group
    Object.keys(grouped).forEach(driverId => {
      grouped[driverId].sort((a, b) => {
        const dateCompare = a.startDate.getTime() - b.startDate.getTime()
        if (dateCompare !== 0) return dateCompare
        return a.startTime.localeCompare(b.startTime)
      })
    })

    return grouped
  })

  // Actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  const setConflictError = (conflict: ConflictError | null) => {
    conflictError.value = conflict
  }

  const clearConflictError = () => {
    conflictError.value = null
  }

  const setScheduleEntries = (entries: ScheduleEntry[]) => {
    scheduleEntries.value = entries
  }

  const addScheduleEntry = (entry: ScheduleEntry) => {
    scheduleEntries.value.push(entry)
    // Sort after adding
    scheduleEntries.value.sort((a, b) => {
      const dateCompare = a.startDate.getTime() - b.startDate.getTime()
      if (dateCompare !== 0) return dateCompare
      return a.startTime.localeCompare(b.startTime)
    })
  }

  const updateScheduleEntryInList = (updatedEntry: ScheduleEntry) => {
    const index = scheduleEntries.value.findIndex(e => e.id === updatedEntry.id)
    if (index !== -1) {
      scheduleEntries.value[index] = updatedEntry
      // Re-sort after updating
      scheduleEntries.value.sort((a, b) => {
        const dateCompare = a.startDate.getTime() - b.startDate.getTime()
        if (dateCompare !== 0) return dateCompare
        return a.startTime.localeCompare(b.startTime)
      })
    }
  }

  const removeScheduleEntryFromList = (entryId: string) => {
    const index = scheduleEntries.value.findIndex(e => e.id === entryId)
    if (index !== -1) {
      scheduleEntries.value.splice(index, 1)
    }
    // Clear selected entry if it was deleted
    if (selectedScheduleEntry.value?.id === entryId) {
      selectedScheduleEntry.value = null
    }
  }

  const setSelectedScheduleEntry = (entry: ScheduleEntry | null) => {
    selectedScheduleEntry.value = entry
  }

  const setSearchFilters = (filters: SearchFilters) => {
    searchFilters.value = { ...filters }
  }

  const clearSearchFilters = () => {
    searchFilters.value = {}
  }

  const setCurrentDateRange = (startDate: Date, endDate: Date) => {
    currentDateRange.value = { startDate, endDate }
  }

  const clearCurrentDateRange = () => {
    currentDateRange.value = null
  }

  // Service integration actions
  const fetchAllScheduleEntries = async (): Promise<void> => {
    try {
      setLoading(true)
      clearError()
      clearConflictError()
      const entries = await scheduleService.getAllScheduleEntries()
      setScheduleEntries(entries)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch schedule entries'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchScheduleEntryById = async (id: string): Promise<ScheduleEntry | null> => {
    try {
      setLoading(true)
      clearError()
      const entry = await scheduleService.getScheduleEntryById(id)
      return entry
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch schedule entry'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const createScheduleEntry = async (entryData: CreateScheduleEntryRequest): Promise<ScheduleEntry> => {
    try {
      setLoading(true)
      clearError()
      clearConflictError()
      const newEntry = await scheduleService.createScheduleEntry(entryData)
      addScheduleEntry(newEntry)

      // Note: LINE notification is sent by ScheduleService.createScheduleEntry()
      // No need to send it here to avoid duplicate notifications

      return newEntry
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create schedule entry'

      // Check if it's a conflict error
      if (errorMessage.includes('ตรวจพบความขัดแย้ง') || errorMessage.includes('conflict')) {
        // This is a simplified conflict error - in a real implementation,
        // the service might return more structured conflict information
        setConflictError({
          type: errorMessage.includes('รถยนต์') || errorMessage.includes('Vehicle') ? 'vehicle' : 'driver',
          conflictingEntry: {} as ScheduleEntry, // Would need actual conflicting entry data
          message: errorMessage
        })
      } else {
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateScheduleEntry = async (id: string, entryData: UpdateScheduleEntryRequest): Promise<ScheduleEntry> => {
    try {
      setLoading(true)
      clearError()
      clearConflictError()

      // Get old entry for comparison
      const oldEntry = scheduleEntries.value.find(e => e.id === id)

      const updatedEntry = await scheduleService.updateScheduleEntry(id, entryData)
      updateScheduleEntryInList(updatedEntry)

      // Update selected entry if it's the one being updated
      if (selectedScheduleEntry.value?.id === id) {
        setSelectedScheduleEntry(updatedEntry)
      }

      // Note: LINE notification is sent by ScheduleService.updateScheduleEntry()
      // No need to send it here to avoid duplicate notifications

      return updatedEntry
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update schedule entry'

      // Check if it's a conflict error
      if (errorMessage.includes('ตรวจพบความขัดแย้ง') || errorMessage.includes('conflict')) {
        setConflictError({
          type: errorMessage.includes('รถยนต์') || errorMessage.includes('Vehicle') ? 'vehicle' : 'driver',
          conflictingEntry: {} as ScheduleEntry,
          message: errorMessage
        })
      } else {
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteScheduleEntry = async (id: string, reason?: string): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      await scheduleService.deleteScheduleEntry(id)
      removeScheduleEntryFromList(id)

      // Note: LINE notification is sent by ScheduleService.deleteScheduleEntry()
      // No need to send it here to avoid duplicate notifications

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete schedule entry'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchScheduleEntriesByDateRange = async (startDate: Date, endDate: Date): Promise<ScheduleEntry[]> => {
    try {
      setLoading(true)
      clearError()
      const entries = await scheduleService.getScheduleEntriesByDateRange(startDate, endDate)
      setCurrentDateRange(startDate, endDate)
      return entries
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch schedule entries by date range'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchScheduleEntriesByVehicle = async (vehicleId: string, startDate?: Date, endDate?: Date): Promise<ScheduleEntry[]> => {
    try {
      setLoading(true)
      clearError()
      const entries = await scheduleService.getScheduleEntriesByVehicle(vehicleId, startDate, endDate)
      return entries
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch schedule entries by vehicle'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchScheduleEntriesByDriver = async (driverId: string, startDate?: Date, endDate?: Date): Promise<ScheduleEntry[]> => {
    try {
      setLoading(true)
      clearError()
      const entries = await scheduleService.getScheduleEntriesByDriver(driverId, startDate, endDate)
      return entries
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch schedule entries by driver'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const searchScheduleEntries = async (filters: SearchFilters): Promise<ScheduleEntry[]> => {
    try {
      setLoading(true)
      clearError()
      const results = await scheduleService.searchScheduleEntries(filters)
      setSearchFilters(filters)
      return results
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search schedule entries'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const checkForConflicts = async (entryData: CreateScheduleEntryRequest, excludeId?: string): Promise<void> => {
    try {
      clearConflictError()
      await scheduleService.checkForConflicts(entryData, excludeId)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Conflict detected'
      
      setConflictError({
        type: errorMessage.includes('Vehicle') ? 'vehicle' : 'driver',
        conflictingEntry: {} as ScheduleEntry,
        message: errorMessage
      })
      throw err
    }
  }



  const transformScheduleEntryFromDatabase = (dbEntry: any): ScheduleEntry => {
    return {
      id: dbEntry.id,
      startDate: new Date(dbEntry.start_date),
      endDate: new Date(dbEntry.end_date),
      isRange: dbEntry.is_range,
      startTime: dbEntry.start_time,
      endTime: dbEntry.end_time,
      vehicleId: dbEntry.vehicle_id,
      driverId: dbEntry.driver_id,
      notes: dbEntry.notes,
      createdAt: new Date(dbEntry.created_at),
      updatedAt: new Date(dbEntry.updated_at),
      // Keep usageDate for backward compatibility
      usageDate: dbEntry.usage_date ? new Date(dbEntry.usage_date) : new Date(dbEntry.start_date),
      // New fields
      timeSlot: dbEntry.time_slot || undefined,
      locations: dbEntry.locations || []
    }
  }

  // Cleanup on store destruction
  onUnmounted(() => {
    // Cleanup if needed
  })

  // Reset store state
  const resetStore = () => {
    scheduleEntries.value = []
    selectedScheduleEntry.value = null
    isLoading.value = false
    error.value = null
    conflictError.value = null
    searchFilters.value = {}
    currentDateRange.value = null
  }

  return {
    // State
    scheduleEntries,
    selectedScheduleEntry,
    isLoading,
    error,
    conflictError,
    searchFilters,
    currentDateRange,

    
    // Getters
    scheduleEntryCount,
    todayScheduleEntries,
    upcomingScheduleEntries,
    pastScheduleEntries,
    filteredScheduleEntries,
    scheduleEntriesByVehicle,
    scheduleEntriesByDriver,
    
    // Actions
    setLoading,
    setError,
    clearError,
    setConflictError,
    clearConflictError,
    setScheduleEntries,
    addScheduleEntry,
    updateScheduleEntryInList,
    removeScheduleEntryFromList,
    setSelectedScheduleEntry,
    setSearchFilters,
    clearSearchFilters,
    setCurrentDateRange,
    clearCurrentDateRange,
    
    // Service integration
    fetchAllScheduleEntries,
    fetchScheduleEntryById,
    createScheduleEntry,
    updateScheduleEntry,
    deleteScheduleEntry,
    fetchScheduleEntriesByDateRange,
    fetchScheduleEntriesByVehicle,
    fetchScheduleEntriesByDriver,
    searchScheduleEntries,
    checkForConflicts,
    

    
    // Utilities
    resetStore
  }
})
